import { useCallback, useEffect, useMemo, useState } from 'react';
import shellData from '../config/ui-shell.json';
import { TENANT_CREDENTIALS_EVENT } from '../config/api-config';
import { useTenantCurrency } from '../context/TenantCurrencyContext';
import { useReferenceDataOptional } from '../context/ReferenceDataContext';
import type { UiShellConfig } from '../context/ConfigContext';
import {
  branchIsHeadquarters,
  branchOptionsFromRows,
  branchesToRows,
} from '../utils/schoolBranches';

export const LS_SELECTED_BRANCH_ID = 'rust-school-selected-branch-id';

/** Product / ERP brand — never a tenant school name. */
export const PLATFORM_HEADER_TITLE =
  (shellData as UiShellConfig).layout.header.title.trim() || 'SkoolFix';

const DEFAULT_HEADER_TITLE = PLATFORM_HEADER_TITLE;

/** Header title saved via Settings → General (localStorage ui-shell). */
export function customHeaderTitleFromSettings(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('ui-shell');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UiShellConfig>;
    const title = parsed.layout?.header?.title?.trim();
    if (!title || title === DEFAULT_HEADER_TITLE) return null;
    return title;
  } catch {
    return null;
  }
}

/** Portal title: Settings override → tenant name → layout default. */
export function resolveHeaderDisplayTitle(configTitle: string, orgName?: string): string {
  const custom = customHeaderTitleFromSettings();
  if (custom) return custom;
  const tenant = orgName?.trim();
  if (tenant) return tenant;
  return configTitle.trim() || DEFAULT_HEADER_TITLE || 'SkoolFix';
}

export type SchoolDisplayNameOptions = {
  /** Platform operators see ERP brand only — ignore tenant / Settings title. */
  platformBranding?: boolean;
};

function readStoredBranchId(): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(LS_SELECTED_BRANCH_ID)?.trim() ?? '';
}

export function useSchoolDisplayName(
  fallbackTitle: string,
  options?: SchoolDisplayNameOptions
) {
  const platformBranding = Boolean(options?.platformBranding);
  const { tenant, loading: tenantLoading } = useTenantCurrency();
  const ref = useReferenceDataOptional();
  const branchesData = ref?.branches.data ?? null;
  const branchesLoading = ref?.branches.loading ?? false;
  const [selectedBranchId, setSelectedBranchId] = useState(readStoredBranchId);

  useEffect(() => {
    const onCredentialsChanged = () => ref?.reloadBranches();
    window.addEventListener(TENANT_CREDENTIALS_EVENT, onCredentialsChanged);
    return () => window.removeEventListener(TENANT_CREDENTIALS_EVENT, onCredentialsChanged);
  }, [ref]);

  const branchRows = useMemo(() => branchesToRows(branchesData ?? null), [branchesData]);
  const branchOptions = useMemo(() => branchOptionsFromRows(branchRows), [branchRows]);
  const hasMultipleBranches = !platformBranding && branchOptions.length > 1;

  useEffect(() => {
    if (platformBranding || branchOptions.length === 0) return;
    const stillValid = branchOptions.some((b) => b.id === selectedBranchId);
    if (stillValid) return;
    const hqRow = branchRows.find(branchIsHeadquarters);
    const fallback =
      branchOptions.find((b) => b.id === branchIdFromHqRow(hqRow)) ?? branchOptions[0];
    setSelectedBranchId(fallback.id);
    localStorage.setItem(LS_SELECTED_BRANCH_ID, fallback.id);
  }, [platformBranding, branchOptions, branchRows, selectedBranchId]);

  const orgName = tenant?.name?.trim() ?? '';
  const settingsTitle = customHeaderTitleFromSettings();

  const displayTitle = useMemo(() => {
    if (platformBranding) return PLATFORM_HEADER_TITLE;
    if (hasMultipleBranches) {
      const selected = branchOptions.find((b) => b.id === selectedBranchId);
      if (selected?.name) return selected.name;
    }
    if (settingsTitle) return settingsTitle;
    if (orgName) return orgName;
    return fallbackTitle.trim() || PLATFORM_HEADER_TITLE;
  }, [
    platformBranding,
    hasMultipleBranches,
    branchOptions,
    selectedBranchId,
    settingsTitle,
    orgName,
    fallbackTitle,
  ]);

  const selectBranch = useCallback((branchId: string) => {
    setSelectedBranchId(branchId);
    localStorage.setItem(LS_SELECTED_BRANCH_ID, branchId);
  }, []);

  return {
    displayTitle,
    orgName,
    orgType: tenant?.org_type,
    hasMultipleBranches,
    branchOptions,
    selectedBranchId,
    selectBranch,
    loading: platformBranding ? false : tenantLoading || branchesLoading,
  };
}

function branchIdFromHqRow(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const rec = row as Record<string, unknown>;
  const id = rec.id ?? rec.branch_id;
  return id != null ? String(id) : '';
}
